import { createContext, useContext } from 'react';

import type { GapDraftView, GapReviewExecutionSummary } from '@momentum/types';

/**
 * The selected run and its drafts, provided once by the layout route.
 *
 * `drafts` is ONE run's — the one named by `executionId` — because that is all
 * this fetches. Every count taken over it is therefore a count of that run,
 * which is what the sidebar chips and the topbar's queue badge mean.
 *
 * The one thing that is not a count of this run is the final-review badge, and
 * it does not come from here: the kept list spans every run and is fetched by
 * the page that draws it. See `useKeptGapDrafts` and `countKeptAcrossRuns`.
 *
 * The layout does not render its `Outlet` until both queries have resolved, so
 * every view below it can treat the data as present instead of repeating the
 * same pending/error branch four times.
 */
export interface GapReviewData {
  /** Every run of the company, newest first. What the picker lists. */
  executions: GapReviewExecutionSummary[];
  /** The selected run's drafts, and only that run's. */
  drafts: GapDraftView[];
  /** The run the drafts belong to. */
  executionId: string;
}

const GapReviewDataContext = createContext<GapReviewData | null>(null);

export function GapReviewDataProvider({
  value,
  children,
}: {
  value: GapReviewData;
  children: React.ReactNode;
}) {
  return (
    <GapReviewDataContext.Provider value={value}>
      {children}
    </GapReviewDataContext.Provider>
  );
}

export function useGapReviewData(): GapReviewData {
  const gapReviewData = useContext(GapReviewDataContext);
  if (!gapReviewData) {
    throw new Error(
      'useGapReviewData must be used inside the /gap-review layout route'
    );
  }
  return gapReviewData;
}
